<template>
  <input-dropdown
    v-bind="$attrs"
    :value="selectedOption"
    :options="labeledOptions"
    :searchable="true"
    item-key="value"
    prop="label"
    @update:value="onUpdate($event.value)"
  ></input-dropdown>
</template>

<script setup lang="ts">
import { computed } from "vue";
import { useI18n } from "vue-i18n";
import { includes } from "lodash-es";

import InputDropdown from "@core/input/InputDropdown.vue";

import type { RoundUnit } from "./SimulationRoundItem";

const commonOptions = [
  "year",
  "halfYear",
  "quarter",
  "month",
  "week",
  "day"
] as const;
const simpleOptions = ["round", ...commonOptions, "custom"] as const;
const extendedOptions = [
  "century",
  "decade",
  ...commonOptions,
  "hour",
  "minute",
  "second"
] as const;

const props = withDefaults(defineProps<{
  type?: "simple" | "extended";
  value?: RoundUnit;
  isCustom?: boolean;
  amount?: number;
  nullChoosable?: boolean;
  roundName?: string | null;
}>(), {
  type: "simple",
  value: "round",
  isCustom: false,
  amount: 1,
  nullChoosable: false,
  roundName: null
});

const emit = defineEmits<{
  (e: "update:value", value: RoundUnit | "custom" | null): void;
  (e: "update:is-custom", value: boolean | null): void;
}>();

const { te, t } = useI18n();

const val = computed(() => {
  if (props.isCustom) {
    return "custom";
  }
  return props.value;
});

const options = computed(() => {
  const opts: (RoundUnit | null | "custom")[] = [];
  if (props.nullChoosable) {
    opts.push(null);
  }
  if (props.type === "simple") return opts.concat(simpleOptions);
  return opts.concat(extendedOptions);
});
const labeledOptions = computed(() => {
  return options.value.map((unit) => {
    if (unit == null) return {
      value: null,
      label: t("actions.reset")
    }
    if (unit === "round") return {
      value: "round",
      label: t(`roundUnits.round`, props.amount) + (props.roundName ? ` (${props.roundName})` : "")
    }
    return {
      value: unit,
      label: te(`roundUnits.${unit}`) ? t(`roundUnits.${unit}`, props.amount) : unit
    }
  });
});
const selectedOption = computed(() => {
  return labeledOptions.value.find((option) => option.value === val.value);
});


function onUpdate(val: RoundUnit | "custom" | null) {
  if (val == null) {
    emit("update:value", null);
    emit("update:is-custom", null);
  } else if (props.type != "simple") {
    emit("update:value", val);
  } else {
    if (val === "custom" || !includes(simpleOptions, val)) {
      emit("update:is-custom", true);
    } else {
      emit("update:value", val);
      emit("update:is-custom", false);
    }
  }
}
</script>
